import { isDefined } from "@clipboard-health/util-ts";
import { z } from "zod";

import { APP_V2_APP_EVENTS, logError } from "../../../lib/analytics";

/**
 * FCM approval status values:
 * -1 = Pending
 * 0 = Rejected/Declined
 * 1 = Approved/Accepted
 */
export const FCM_APPROVAL_STATUS = {
  PENDING: -1,
  REJECTED: 0,
  APPROVED: 1,
} as const;

export type FcmApprovalStatus = (typeof FCM_APPROVAL_STATUS)[keyof typeof FCM_APPROVAL_STATUS];

const fcmRequestMessageDataSchema = z.object({
  shiftId: z.string(),
  approvalStatus: z.number(),
  description: z.string().optional(),
  workerName: z.string().optional(),
});

export type FcmRequestMessageData = z.infer<typeof fcmRequestMessageDataSchema>;

export interface ParsedFcmRequestMessage {
  shiftId: string;
  approvalStatus: FcmApprovalStatus;
  description: string | undefined;
  workerName: string | undefined;
}

/**
 * Check if a string is valid JSON
 */
function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Extract worker name from FCM message text as a fallback.
 * Message format: "Can you confirm your facility sent {workerName} home?"
 */
function extractNameFromMessageText(messageText: string): string | undefined {
  const match = /Can you confirm your facility sent (.+?) home\?/.exec(messageText);
  if (!isDefined(match)) {
    return undefined;
  }
  return match[1].trim();
}

/**
 * Extract description from FCM message text as a fallback.
 * Message format: "...provided the following details with this request: {description}. Can..."
 */
function extractDescriptionFromMessageText(messageText: string): string | undefined {
  const match = /provided the following details with this request: (.+?)\. Can/.exec(messageText);
  if (!isDefined(match)) {
    return undefined;
  }
  return match[1].trim();
}

/**
 * Parse FCM (Facility Canceled Me / Sent Home) request data from the admin message.
 * Returns parsed data with shiftId, approvalStatus, description, and workerName.
 *
 * For older messages that don't include workerName and description in the data payload,
 * these values are extracted from the message text using regex patterns.
 */
export function parseFcmRequestMessageData(
  data: string | undefined,
  messageText: string
): ParsedFcmRequestMessage | undefined {
  if (!isDefined(data) || data === "") {
    logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
      error: new Error("FCM request message has no data"),
      metadata: { messageText },
    });
    return undefined;
  }

  if (!isValidJson(data)) {
    logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
      error: new Error("FCM request message data is not valid JSON"),
      metadata: { data, messageText },
    });
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(data);
    const validationResult = fcmRequestMessageDataSchema.safeParse(parsed);

    if (!validationResult.success) {
      logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
        error: new Error("FCM request message data validation failed"),
        metadata: { data, messageText, validationError: validationResult.error },
      });
      return undefined;
    }

    const messageData = validationResult.data;

    // For older messages, extract workerName and description from message text as fallback
    const workerName = messageData.workerName ?? extractNameFromMessageText(messageText);
    const description = messageData.description ?? extractDescriptionFromMessageText(messageText);

    return {
      shiftId: messageData.shiftId,
      approvalStatus: messageData.approvalStatus as FcmApprovalStatus,
      description,
      workerName,
    };
  } catch (error) {
    logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
      error: error instanceof Error ? error : new Error("Failed to parse FCM request message data"),
      metadata: { data, messageText },
    });
    return undefined;
  }
}
